import React, { useRef, useState, useEffect } from 'react';
import './MacPaint.css';

type Tool = 'brush' | 'pencil' | 'eraser' | 'bucket' | 'line' | 'rectangle' | 'circle' | 'select' | 'text';
type Pattern = 'solid' | 'dots' | 'diagonal' | 'grid' | 'waves';

interface Point {
  x: number;
  y: number;
}

const MacPaint: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const [currentTool, setCurrentTool] = useState<Tool>('brush');
  const [currentPattern, setCurrentPattern] = useState<Pattern>('solid');
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState<Point | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPatternFill = (ctx: CanvasRenderingContext2D, pattern: Pattern): CanvasPattern | string => {
    if (pattern === 'solid') return 'black';
    
    const patternCanvas = document.createElement('canvas');
    const patternCtx = patternCanvas.getContext('2d')!;
    patternCanvas.width = 8;
    patternCanvas.height = 8;
    
    patternCtx.fillStyle = 'white';
    patternCtx.fillRect(0, 0, 8, 8);
    patternCtx.fillStyle = 'black';

    switch (pattern) {
      case 'dots':
        patternCtx.fillRect(2, 2, 1, 1);
        patternCtx.fillRect(6, 6, 1, 1);
        break;
      case 'diagonal':
        for (let i = 0; i < 8; i++) {
          patternCtx.fillRect(i, i, 1, 1);
        }
        break;
      case 'grid':
        patternCtx.fillRect(0, 0, 8, 1);
        patternCtx.fillRect(0, 4, 8, 1);
        patternCtx.fillRect(0, 0, 1, 8);
        patternCtx.fillRect(4, 0, 1, 8);
        break;
      case 'waves':
        patternCtx.fillRect(0, 2, 2, 1);
        patternCtx.fillRect(2, 1, 2, 1);
        patternCtx.fillRect(4, 2, 2, 1);
        patternCtx.fillRect(6, 3, 2, 1);
        break;
    }

    return ctx.createPattern(patternCanvas, 'repeat') || 'black';
  };

  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const point = getMousePos(e);
    
    if (currentTool === 'text') {
      setTextPosition(point);
      setIsTyping(true);
      setTextInput('');
      setTimeout(() => textInputRef.current?.focus(), 0);
      return;
    }

    setIsDrawing(true);
    setStartPoint(point);

    if (currentTool === 'brush' || currentTool === 'pencil') {
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const point = getMousePos(e);

    switch (currentTool) {
      case 'brush':
        ctx.lineWidth = 4;
        ctx.strokeStyle = getPatternFill(ctx, currentPattern);
        ctx.lineTo(point.x, point.y);
        ctx.stroke();
        break;
      
      case 'pencil':
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'black';
        ctx.lineTo(point.x, point.y);
        ctx.stroke();
        break;
      
      case 'eraser':
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.arc(point.x, point.y, 8, 0, 2 * Math.PI);
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
        break;
    }
  };

  const stopDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPoint) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const endPoint = getMousePos(e);
    
    switch (currentTool) {
      case 'line':
        ctx.beginPath();
        ctx.moveTo(startPoint.x, startPoint.y);
        ctx.lineTo(endPoint.x, endPoint.y);
        ctx.lineWidth = 2;
        ctx.strokeStyle = getPatternFill(ctx, currentPattern);
        ctx.stroke();
        break;
      
      case 'rectangle':
        const width = endPoint.x - startPoint.x;
        const height = endPoint.y - startPoint.y;
        ctx.lineWidth = 2;
        ctx.strokeStyle = getPatternFill(ctx, currentPattern);
        ctx.strokeRect(startPoint.x, startPoint.y, width, height);
        break;
      
      case 'circle':
        const radius = Math.sqrt(
          Math.pow(endPoint.x - startPoint.x, 2) + Math.pow(endPoint.y - startPoint.y, 2)
        );
        ctx.beginPath();
        ctx.arc(startPoint.x, startPoint.y, radius, 0, 2 * Math.PI);
        ctx.lineWidth = 2;
        ctx.strokeStyle = getPatternFill(ctx, currentPattern);
        ctx.stroke();
        break;
      
      case 'bucket':
        ctx.fillStyle = getPatternFill(ctx, currentPattern);
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        break;
    }

    setIsDrawing(false);
    setStartPoint(null);
  };

  const handleTextSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && textPosition) {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx || !textInput.trim()) return;

      ctx.font = '12px Monaco, monospace';
      ctx.fillStyle = 'black';
      ctx.fillText(textInput, textPosition.x, textPosition.y);
      
      setIsTyping(false);
      setTextInput('');
      setTextPosition(null);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const exportImage = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        if (!blob) return;

        const formData = new FormData();
        formData.append('image', blob, 'macpaint-creation.png');
        formData.append('timestamp', new Date().toISOString());

        // Upload to backend
        const response = await fetch('http://localhost:3001/api/upload', {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          const result = await response.json();
          alert(`Image uploaded successfully! ID: ${result.id}`);
        } else {
          alert('Upload failed. Please try again.');
        }
      }, 'image/png');
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    }
  };

  return (
    <div className="macpaint">
      {/* Simple Menu Bar */}
      <div className="macpaint-menubar">
        <span>File</span>
        <span>Edit</span>
        <span>Paint</span>
      </div>
      
      <div className="macpaint-workspace">
        {/* Tools at top left */}
        <div className="macpaint-tools">
          <button 
            className={`tool-btn ${currentTool === 'select' ? 'active' : ''}`}
            onClick={() => setCurrentTool('select')}
          >
            □
          </button>
          <button 
            className={`tool-btn ${currentTool === 'pencil' ? 'active' : ''}`}
            onClick={() => setCurrentTool('pencil')}
          >
            ✏
          </button>
          <button 
            className={`tool-btn ${currentTool === 'brush' ? 'active' : ''}`}
            onClick={() => setCurrentTool('brush')}
          >
            ▓
          </button>
          <button 
            className={`tool-btn ${currentTool === 'text' ? 'active' : ''}`}
            onClick={() => setCurrentTool('text')}
          >
            A
          </button>
          <button 
            className={`tool-btn ${currentTool === 'eraser' ? 'active' : ''}`}
            onClick={() => setCurrentTool('eraser')}
          >
            ▤
          </button>
          <button 
            className={`tool-btn ${currentTool === 'line' ? 'active' : ''}`}
            onClick={() => setCurrentTool('line')}
          >
            /
          </button>
          <button 
            className={`tool-btn ${currentTool === 'rectangle' ? 'active' : ''}`}
            onClick={() => setCurrentTool('rectangle')}
          >
            ▫
          </button>
          <button 
            className={`tool-btn ${currentTool === 'circle' ? 'active' : ''}`}
            onClick={() => setCurrentTool('circle')}
          >
            ○
          </button>
          <button 
            className={`tool-btn ${currentTool === 'bucket' ? 'active' : ''}`}
            onClick={() => setCurrentTool('bucket')}
          >
            ■
          </button>
        </div>

        {/* Canvas Area */}
        <div className="macpaint-canvas-container">
          <canvas
            ref={canvasRef}
            width={420}
            height={280}
            className="macpaint-canvas"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
          />
          
          {/* Text input overlay */}
          {isTyping && textPosition && (
            <input
              ref={textInputRef}
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={handleTextSubmit}
              onBlur={() => setIsTyping(false)}
              className="text-input"
              style={{
                position: 'absolute',
                left: textPosition.x,
                top: textPosition.y - 12,
                background: 'transparent',
                border: '1px dashed #666',
                font: '12px Monaco, monospace',
                outline: 'none'
              }}
            />
          )}
        </div>

        {/* Patterns at bottom */}
        <div className="macpaint-patterns">
          <button 
            className={`pattern-btn ${currentPattern === 'solid' ? 'active' : ''}`}
            onClick={() => setCurrentPattern('solid')}
          >
            <div className="pattern-solid"></div>
          </button>
          <button 
            className={`pattern-btn ${currentPattern === 'dots' ? 'active' : ''}`}
            onClick={() => setCurrentPattern('dots')}
          >
            <div className="pattern-dots"></div>
          </button>
          <button 
            className={`pattern-btn ${currentPattern === 'diagonal' ? 'active' : ''}`}
            onClick={() => setCurrentPattern('diagonal')}
          >
            <div className="pattern-diagonal"></div>
          </button>
          <button 
            className={`pattern-btn ${currentPattern === 'grid' ? 'active' : ''}`}
            onClick={() => setCurrentPattern('grid')}
          >
            <div className="pattern-grid-fill"></div>
          </button>
          <button 
            className={`pattern-btn ${currentPattern === 'waves' ? 'active' : ''}`}
            onClick={() => setCurrentPattern('waves')}
          >
            <div className="pattern-waves"></div>
          </button>
        </div>
      </div>
      
      {/* Action buttons */}
      <div className="macpaint-actions">
        <button onClick={clearCanvas} className="clear-btn">Clear</button>
        <button onClick={exportImage} className="export-btn">Export</button>
      </div>
    </div>
  );
};

export default MacPaint;